import {useEffect, useMemo, useState, useCallback, useRef} from 'react';
import PropTypes from 'prop-types';
import {Helmet} from 'react-helmet';
import {Divider, LinearProgress} from '@material-ui/core';
import {convertToObjectByKey} from '@ozgene/gp5.utils.array-functions';
import {debounce} from 'throttle-debounce';
import SbsLobbyActionToolbar from 'SbsLobby/Components/SbsLobbyActionToolbar';
import SbsList from 'SbsLobby/Components/SbsList';
import {findSbsBySearch} from 'Sbs/service/SbsService';

const convertFilterToIndividual = (filters) => {
  const decodedFilters = decodeURIComponent(filters);

  if (decodedFilters) {
    const parsedFilters = JSON.parse(decodedFilters);
    const teamsFilter =
      parsedFilters.find((filter) => filter.columnName === 'teams') || {};
    const titleFilter =
      parsedFilters.find((filter) => filter.columnName === 'title') || {};

    return {
      convertedTitleFilterText: titleFilter.value,
      convertedTeamFilterText: teamsFilter.value?.name,
    };
  }

  return {};
};

export default function SbsLobbyView(props, context) {
  const {router} = context;
  const {
    location: {query, pathname},
  } = props;
  const listRef = useRef(null);
  const {sbsTitleFilterText = '', sbsTeamFilterText = '', filters = ''} = query;
  const {convertedTitleFilterText, convertedTeamFilterText} =
    convertFilterToIndividual(filters);
  const decodedSbsTitleFilterText = decodeURIComponent(sbsTitleFilterText);
  const decodedSbsTeamFilterText = decodeURIComponent(sbsTeamFilterText);
  const [sbss, setSbss] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedSbsId, setSelectedSbsId] = useState();
  const sbsById = useMemo(() => convertToObjectByKey(sbss, 'id'), [sbss]);
  const selectedSbs = useMemo(
    () => sbsById[selectedSbsId],
    [selectedSbsId, sbsById],
  );
  const handleSelectionChange = (selection) => {
    if (!!selection && selection.length > 0) {
      setSelectedSbsId(selection[selection.length - 1]);
    } else {
      setSelectedSbsId();
    }
  };
  const handleChangeSbsTitleFilter = debounce(1000, (titleFilterText) => {
    router.replace({
      pathname,
      query: {
        sbsTitleFilterText: encodeURIComponent(titleFilterText) || undefined,
        sbsTeamFilterText: sbsTeamFilterText || undefined,
      },
    });
  });
  const handleChangeSbsTeamFilter = useCallback(
    (teamFilterText) => {
      router.replace({
        pathname,
        query: {
          sbsTitleFilterText: sbsTitleFilterText || undefined,
          sbsTeamFilterText: encodeURIComponent(teamFilterText) || undefined,
        },
      });
    },
    [pathname, sbsTitleFilterText, router],
  );
  const handleExportList = useCallback(() => {
    listRef.current.exportGrid();
  }, [listRef]);

  useEffect(() => {
    const handleRetrieveSbss = async () => {
      setIsLoading(true);
      const apiQuery = decodedSbsTeamFilterText
        ? decodedSbsTitleFilterText
          ? {
              teamName: decodedSbsTeamFilterText,
              title: decodedSbsTitleFilterText,
            }
          : {teamName: decodedSbsTeamFilterText}
        : {title: decodedSbsTitleFilterText};
      const newSbss = await findSbsBySearch(apiQuery);

      setSbss(newSbss);
      setIsLoading(false);
    };

    if (
      (decodedSbsTitleFilterText && decodedSbsTitleFilterText.length >= 2) ||
      decodedSbsTeamFilterText
    ) {
      handleRetrieveSbss();
    } else {
      setSbss([]);
    }
  }, [decodedSbsTeamFilterText, decodedSbsTitleFilterText]);

  useEffect(() => {
    if (convertedTitleFilterText || convertedTeamFilterText) {
      router.replace({
        pathname,
        query: {
          sbsTitleFilterText:
            encodeURIComponent(convertedTitleFilterText) || undefined,
          sbsTeamFilterText:
            encodeURIComponent(convertedTeamFilterText) || undefined,
        },
      });
    }
  }, [convertedTeamFilterText, convertedTitleFilterText, pathname, router]);

  return (
    <div>
      <Helmet title="SBSs" defer={false} />
      <SbsLobbyActionToolbar
        sbss={sbss}
        selectedSbs={selectedSbs}
        onExportList={handleExportList}
      />
      {isLoading && <LinearProgress color="secondary" />}
      <Divider />
      <SbsList
        ref={listRef}
        sbss={sbss}
        isLoading={isLoading}
        sbsTeamFilter={decodedSbsTeamFilterText}
        sbsTitleFilter={decodedSbsTitleFilterText}
        onChangeSbsTitleFilter={handleChangeSbsTitleFilter}
        onChangeSbsTeamFilter={handleChangeSbsTeamFilter}
        onChangeSelection={handleSelectionChange}
        selectedSbsId={selectedSbsId}
      />
    </div>
  );
}

SbsLobbyView.propTypes = {
  location: PropTypes.shape({
    pathname: PropTypes.string,
    query: PropTypes.shape({
      sbsTitleFilterText: PropTypes.string,
      sbsTeamFilterText: PropTypes.string,
      filters: PropTypes.string,
    }),
  }).isRequired,
};

SbsLobbyView.contextTypes = {
  router: PropTypes.object.isRequired,
};
